import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { BiPlus } from 'react-icons/bi'

import { Folder } from '../../api/services/dietbase/types.ts'
import Accordion, { AccordionChildren } from '../../components/common/Accordion'
import Loader from '../../components/common/Loader/Loader.tsx'
import TailButton from '../../components/common/TailButton/TailButton.tsx'
import useDietBaseFolders from '../../hooks/useDietBaseFolders.ts'
import AddFolder from './AddFolder.tsx'
import DietList from './DietList.tsx'
import FolderItem from './FolderItem.tsx'

const Folders = ({
  categoryID,
  isGlobal
}: {
  categoryID: number
  isGlobal: boolean
}) => {
  const {
    loading,
    fetchFolders,
    createNewFolder,
    editFolderDetails,
    removeFolder,
    lastAddedFolder
  } = useDietBaseFolders()

  const [edited, setEdited] = useState<number | null>(null)
  const [addNewFolder, setAddNewFolder] = useState(false)
  const [folders, setFolders] = useState<Folder[]>([])
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null)
  const [selectedCalories, setSelectedCalories] = useState<string | null>(null)
  const [selectedMeals, setSelectedMeals] = useState<string | null>(null)

  useLayoutEffect(() => {
    const fetchData = async () => {
      const { folders } = await fetchFolders(categoryID)
      setFolders(folders)
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (lastAddedFolder) {
      setFolders((prevFolders) => {
        if (prevFolders) {
          return [...prevFolders, lastAddedFolder]
        }
        return [lastAddedFolder]
      })
    }
  }, [lastAddedFolder])

  const saveFolder = useCallback(() => {
    if (edited === null && addNewFolder && selectedCalories && selectedMeals) {
      createNewFolder(categoryID, selectedCalories, selectedMeals)
    }
    if (edited !== null && selectedCalories && selectedMeals) {
      editFolderDetails(
        edited,
        categoryID,
        selectedCalories,
        selectedMeals
      ).then(() => {
        setFolders((prevFolders) => {
          const index = prevFolders.findIndex((folder) => folder.id === edited)
          if (index !== -1) {
            const newFolders = [...prevFolders]
            newFolders[index].kcal = parseInt(selectedCalories)
            newFolders[index].number_of_meals = parseInt(selectedMeals)
            return newFolders
          }
          return prevFolders
        })
      })
    }
    setSelectedMeals(null)
    setSelectedCalories(null)
    setAddNewFolder(false)
    setEdited(null)
  }, [edited, addNewFolder, selectedCalories, selectedMeals])

  const handleSelect = (props: AccordionChildren) => {
    props.setSqueezed(!props.squeezed)
    props.setLabel(props.newLabel)
  }

  const prepareEditFolder = (folderID: number) => {
    setEdited(folderID)
  }

  const removeFolderHandler = async (folderID: number) => {
    const isRemoved = await removeFolder(folderID as number)
    if (!isRemoved) return

    setFolders((prevFolder) => {
      return prevFolder.filter((folder) => folder.id !== folderID)
    })
  }

  return (
    <React.Fragment>
      <Accordion
        title={'Foldery'}
        onSqueezeChange={(squeezed) => {
          if (!squeezed) setSelectedFolder(null)
        }}
      >
        {({ squeezed, setSqueezed, setLabel, toggleSqueeze }) => (
          <div
            className={
              'flex items-center justify-center md:justify-start flex-wrap gap-4 mt-4'
            }
          >
            {loading && (
              <div className={'flex justify-center'}>
                <Loader size="large" />
              </div>
            )}
            {folders &&
              folders.length > 0 &&
              folders.map((folder, index) => (
                <div
                  key={folder.id}
                  onClick={() =>
                    !addNewFolder && edited === null
                      ? handleSelect({
                          squeezed,
                          setSqueezed,
                          setLabel,
                          newLabel: `${folder.folder_category.toString()} (Liczba posiłków: ${folder.number_of_meals.toString()}, liczba kalorii: ${folder.kcal.toString()})`,
                          toggleSqueeze
                        })
                      : undefined
                  }
                >
                  <TailButton
                    key={folder.id}
                    id={folder.id}
                    title={''}
                    tailSelected={selectedFolder}
                    setTailSelected={setSelectedFolder}
                    disabled={
                      addNewFolder || (edited !== null && edited !== folder.id)
                    }
                    hintLabel={' (Liczba diet: ' + folder.diets_count + ')'}
                    className={'w-50  bg-gray-50 dark:bg-gray-800 rounded pt-8'}
                    onEdit={
                      !isGlobal && folder.id !== edited && !addNewFolder
                        ? () => prepareEditFolder(folder.id)
                        : undefined
                    }
                    onDelete={
                      !isGlobal && folder.id !== edited && !addNewFolder
                        ? () => removeFolderHandler(folder.id)
                        : undefined
                    }
                  >
                    {edited !== folder.id && (
                      <FolderItem
                        folder={folder}
                        selectedFolder={selectedFolder}
                        index={index}
                      />
                    )}

                    {edited === folder.id && (
                      <div className={'py-0 -mt-2'}>
                        <AddFolder
                          edited={edited}
                          setEdited={setEdited}
                          selectedCalories={folder.kcal.toString()}
                          setSelectedCalories={setSelectedCalories}
                          selectedMeals={folder.number_of_meals.toString()}
                          setSelectedMeals={setSelectedMeals}
                          saveFolder={saveFolder}
                          setAddNewFolder={setAddNewFolder}
                        />
                      </div>
                    )}
                  </TailButton>
                </div>
              ))}

            {!isGlobal && (
              <TailButton
                key={9999}
                id={9999}
                title={!addNewFolder ? 'Dodaj nowy folder' : ''}
                icon={
                  !addNewFolder ? (
                    <BiPlus size={50} color={'#16a34a'} />
                  ) : undefined
                }
                externalFunction={() => setAddNewFolder(true)}
                className={'w-50'}
                disabled={edited !== null}
              >
                {addNewFolder && (
                  <div>
                    <AddFolder
                      edited={edited}
                      setEdited={setEdited}
                      selectedCalories={selectedCalories}
                      setSelectedCalories={setSelectedCalories}
                      selectedMeals={selectedMeals}
                      setSelectedMeals={setSelectedMeals}
                      saveFolder={saveFolder}
                      setAddNewFolder={setAddNewFolder}
                    />
                  </div>
                )}
              </TailButton>
            )}
          </div>
        )}
      </Accordion>

      {selectedFolder !== null && (
        <DietList isGlobal={isGlobal} folderID={selectedFolder} />
      )}
    </React.Fragment>
  )
}

export default Folders
